<template>
  <v-dialog :value="visible" max-width="1160" persistent>
    <v-card v-if="selectedAppointment" class="detail-appointment">
      <!-- Appointment Header -->
      <v-card-title class="d-flex justify-space-between align-center detail-head">
        <div>
          <div class="detail-title">
            Appointment Details — <span class="ref-highlight">#{{ selectedAppointment.apt_code || '-' }}</span>
          </div>
          <div class="detail-sub d-flex align-center gap-2 mt-1">
            <span class="appointment-branch-tag">{{ getBranchCode(selectedAppointment) }}</span>
            <span class="dot-sep">•</span>
            <span class="meta-text">Booked via {{ selectedAppointment.display_type }}</span>
            <span class="dot-sep">•</span>
            <span class="meta-text">{{ formatAppointmentDateTime(selectedAppointment) }}</span>
          </div>
        </div>
        <v-btn icon class="close-btn" @click="closeAppointment">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Appointment Detail Body (2-Column Grid Layout) -->
      <div class="detail-body">
        <!-- Loading State -->
        <div v-if="isAppointmentLoading" class="appointment-loading-wrap">
          <v-progress-circular indeterminate color="primary" size="44"></v-progress-circular>
          <div class="mt-3 text-caption grey--text font-weight-medium">Loading appointment details...</div>
        </div>

        <!-- LEFT COLUMN: Appointment Summary & Patient Information -->
        <div v-else class="left-col" ref="leftCol">
          <!-- 1. APPOINTMENT SUMMARY CARD -->
          <div class="summary-section-label">APPOINTMENT SUMMARY</div>

          <div class="appointment-summary-card pa-4 mb-4">
            <!-- Top Row: Date/Time + Booking Badge -->
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
                <span class="summary-datetime-text">
                  {{ formatTableDate(selectedAppointment.apt_date) }}, {{ formatTableTime(selectedAppointment.apt_start_time) }} – {{ formatTableTime(selectedAppointment.apt_end_time) }}
                </span>
              </div>
              <span :class="['type-pill', bookingTypeClass(selectedAppointment)]">
                {{ formatAppointmentBookingType(selectedAppointment) }}
              </span>
            </div>

            <!-- Department & Assigned Doctor Row -->
            <div class="grid-2 mb-3">
              <div>
                <label>DEPARTMENT</label>
                <p class="summary-field-val">{{ getDepartmentName(selectedAppointment) }}</p>
              </div>
              <div>
                <label>ASSIGNED DOCTOR</label>
                <p class="summary-field-val">{{ getDoctorName(selectedAppointment) }}</p>
              </div>
            </div>

            <!-- Patient Symptoms -->
            <div class="mb-3">
              <label>PATIENT SYMPTOMS / NOTES</label>
              <div class="symptoms-content-box mt-1">
                {{ selectedAppointment.apt_description || 'No specific symptoms or notes provided by the patient.' }}
              </div>
            </div>

            <!-- Attached Files Section -->
            <div class="attached-files-wrap">
              <label>ATTACHED FILES</label>
              <div v-if="selectedAppointment.apt_files && selectedAppointment.apt_files.length" class="mt-1">
                <div
                  v-for="(file, idx) in selectedAppointment.apt_files"
                  :key="idx"
                  class="file-attachment-card d-flex align-center justify-space-between pa-2 px-3 mb-2"
                >
                  <div class="d-flex align-center text-truncate mr-2">
                    <v-icon small color="primary" class="mr-2">mdi-file-document-outline</v-icon>
                    <span class="file-name text-truncate">{{ getFileName(file) }}</span>
                  </div>
                  <div class="d-flex align-center flex-shrink-0">
                    <span class="file-size mr-3">{{ getFileSize(file) }}</span>
                    <a  v-if="getFileUrl(file)" :href="getFileUrl(file)" target="_blank" download class="download-link font-weight-bold">
                      Download
                    </a>
                  </div>
                </div>
              </div>
              <div v-else class="empty-files-box mt-1">
                <v-icon small class="mr-1 text--secondary">mdi-paperclip</v-icon>
                <span>No attached medical records or referral letters.</span>
              </div>
            </div>
          </div>

          <!-- 2. SUBMITTER CARD (If booked for other) -->
          <div v-if="isBookedForOther(selectedAppointment)" class="submitter-card pa-4 mb-4">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="submitter-badge-title">SUBMITTER / CONTACT PERSON</span>
              <span class="relationship-badge">{{ getSubmitterRelationship(selectedAppointment) }} OF PATIENT</span>
            </div>
            <div class="grid-2">
              <div>
                <label>SUBMITTER FULL NAME</label>
                <p>{{ getSubmitterName(selectedAppointment) }}</p>
              </div>
              <div>
                <label>RELATIONSHIP TO PATIENT</label>
                <p>{{ getSubmitterRelationship(selectedAppointment) }}</p>
              </div>
              <div>
                <label>CONTACT PHONE</label>
                <p class="font-weight-bold">{{ getSubmitterPhone(selectedAppointment) }}</p>
              </div>
              <div>
                <label>CONTACT EMAIL</label>
                <p>{{ getSubmitterEmail(selectedAppointment) }}</p>
              </div>
            </div>
          </div>

          <!-- 3. PATIENT PROFILE -->
          <div class="patient-profile-section mb-4">
            <div class="profile-header-title mb-3">PATIENT PROFILE</div>
            <div class="grid-2 mb-3">
              <div>
                <label>TITLE / FIRST NAME</label>
                <p class="profile-val font-weight-bold">{{ getPatientTitleFirstName(selectedAppointment) }}</p>
              </div>
              <div>
                <label>LAST NAME</label>
                <p class="profile-val font-weight-bold">{{ getPatientLastName(selectedAppointment) }}</p>
              </div>
              <div>
                <label>GENDER</label>
                <p class="profile-val font-weight-bold">{{ getPatientGender(selectedAppointment) }}</p>
              </div>
              <div>
                <label>BIRTH DATE</label>
                <p class="profile-val font-weight-bold">
                  {{ formatBirthdate(getPatientBirthdate(selectedAppointment)) }}
                  <span v-if="calculateAge(getPatientBirthdate(selectedAppointment))">
                    ({{ calculateAge(getPatientBirthdate(selectedAppointment)) }})
                  </span>
                </p>
              </div>
              <div>
                <label>PHONE</label>
                <p class="profile-val font-weight-bold">{{ getPatientPhone(selectedAppointment) }}</p>
              </div>
              <div>
                <label>EMAIL</label>
                <p class="profile-val font-weight-bold">{{ getPatientEmail(selectedAppointment) }}</p>
              </div>
            </div>
            <!-- National ID with Masking & Reveal Toggle -->
            <div class="mt-3">
              <label>NATIONAL ID / PASSPORT NO.</label>
              <div class="id-display-row mt-1">
                {{ isIdRevealed ? formatIdCard(getPatientIdCard(selectedAppointment)) : maskIdCard(getPatientIdCard(selectedAppointment)) }}
              </div>
              <div class="security-warning-box d-flex align-center justify-space-between mt-2">
                <div class="d-flex align-center">
                  <v-icon small class="lock-icon mr-2">mdi-lock-outline</v-icon>
                  <span class="security-text">Masked — decrypted for authorized admin roles only</span>
                </div>
                <button type="button" class="reveal-btn" @click="isIdRevealed = !isIdRevealed">
                  {{ isIdRevealed ? 'Hide' : 'Reveal' }}
                </button>
              </div>
            </div>
          </div>

          <hr class="sep" />

          <!-- EMERGENCY CONTACT -->
          <div class="emergency-contact-section mb-4">
            <div class="profile-header-title mb-3">EMERGENCY CONTACT</div>
            <div class="grid-2 mb-3">
              <div>
                <label>CONTACT NAME</label>
                <p class="profile-val font-weight-bold">{{ getEmergencyContactName(selectedAppointment) }}</p>
              </div>
              <div>
                <label>PHONE</label>
                <p class="profile-val font-weight-bold">{{ getEmergencyContactPhone(selectedAppointment) }}</p>
              </div>
            </div>
            <div>
              <label>EMAIL</label>
              <p class="profile-val font-weight-bold">{{ getEmergencyContactEmail(selectedAppointment) }}</p>
            </div>
          </div>

          <hr class="sep" />

          <!-- 4. PDPA LEGAL CONSENT AUDIT -->
          <div class="pdpa-audit">
            <div class="pdpa-audit-head mb-3">
              <v-icon small class="pdpa-lock-icon mr-2">mdi-lock-outline</v-icon>
              <span class="pdpa-audit-title">PDPA Legal Consent Audit</span>
            </div>
            <div class="pdpa-audit-grid">
              <div class="audit-item">
                <label class="audit-label">CONSENT STATUS</label>
                <p>
                  <span class="consent-chip accepted">
                    <v-icon x-small class="consent-icon mr-1">mdi-check</v-icon>
                    Accepted
                  </span>
                </p>
              </div>
              <div class="audit-item">
                <label class="audit-label">POLICY VERSION</label>
                <p class="mono-val">pdpa-2026-01</p>
              </div>
              <div class="audit-item">
                <label class="audit-label">CONSENT TIMESTAMP</label>
                <p class="mono-val">{{ formatConsentTimestamp(selectedAppointment.created_at || selectedAppointment.apt_date) }}</p>
              </div>
            </div>
            <div class="audit-footnote">
              Captured automatically at booking submission for PDPA legal proof of consent. Read-only.
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN: Case Actions, Staff Admin Notes & Audit Trail -->
        <div v-if="!isAppointmentLoading" class="right-col" ref="rightCol">
          <div class="section-title">Admin Case Actions</div>

          <!-- Case Status Dropdown -->
          <div class="mb-4">
            <label>CASE STATUS</label>
            <v-select
              v-model="editStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              color="primary"
              item-color="primary"
              class="status-edit-select"
            ></v-select>
          </div>

          <!-- Admin Notes (Quill Editor) -->
          <div class="mb-4">
            <label>ADMIN NOTES (บันทึกภายใน)</label>
            <quill-editor
              ref="adminNotesEditor"
              v-model="editAdminNotes"
              :options="adminNotesEditorOption"
              class="admin-notes-editor"
            ></quill-editor>
            <div class="hint-text">Visible to admin staff only. Not shared with the patient.</div>
          </div>

          <!-- Activity Log Section Header -->
          <div class="activity-header d-flex align-center mt-5 mb-3">
            <span class="activity-header-title">ACTIVITY LOG</span>
          </div>

          <!-- Activity Log Timeline -->
          <div class="activity-timeline" v-if="displayActivityLogs && displayActivityLogs.length">
            <div
              v-for="(log, idx) in displayActivityLogs"
              :key="log.id || idx"
              class="activity-item"
            >
              <span class="activity-bullet" :class="getActivityBulletClass(log.action_type)"></span>
              <div class="activity-content">
                <div class="activity-title" v-html="getActivityTitle(log)"></div>
                <div class="activity-meta">
                  <span>{{ formatActivityTime(log.created_at) }}</span>
                  <span class="meta-dot">·</span>
                  <span>by {{ log.updated_by }}</span>
                  <span v-if="log.action_type" class="activity-badge ml-2">{{ log.action_type }}</span>
                </div>
                <div v-if="log.admin_notes && log.admin_notes.trim()" class="activity-note-box" v-html="log.admin_notes"></div>
                <hr v-if="idx < displayActivityLogs.length - 1" class="activity-divider" />
              </div>
            </div>
          </div>
          <div v-else class="activity-empty-box">
            <v-icon small color="#94a3b8" class="mr-1">mdi-history</v-icon>
            <span>No Activity Logs</span>
          </div>
        </div>
      </div>

      <!-- Appointment Footer -->
      <v-card-actions class="detail-foot">
        <v-spacer></v-spacer>
        <v-btn outlined class="cancel-btn text-none" @click="closeAppointment">Cancel</v-btn>
        <v-btn
          color="primary"
          class="save-btn text-none ml-3 px-6"
          :loading="isSavingAppointment"
          :disabled="!canSaveDetail"
          @click="saveAppointmentChanges"
        >
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import Vue from 'vue'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { quillEditor } from 'vue-quill-editor'

const ACTIVITY_ACTION_CONFIG = {
  STATUS_CHANGE: {
    bulletClass: 'bullet-blue',
    badgeText: 'STATUS_CHANGE',
    getTitle: (log) => `Status changed <b class="font-weight-bold">${log.status_from}</b> <span class="status-arrow">→</span> <b class="font-weight-bold">${log.status_to}</b>`
  },
  NOTE_ADDED: {
    bulletClass: 'bullet-amber',
    badgeText: 'NOTE_ADDED',
    getTitle: () => 'Admin note added'
  },
  CREATED: {
    bulletClass: 'bullet-gray',
    badgeText: 'CREATED',
    getTitle: (log) => `Booking submitted with status <b class="font-weight-bold">${log.status_to}</b>`
  }
}

export default {
  name: 'AppointmentDetail',
  components: {
    quillEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    appointmentItem: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      selectedAppointment: null,
      isAppointmentLoading: false,
      isSavingAppointment: false,
      isIdRevealed: false,
      editStatus: 'pending',
      editAdminNotes: '',
      statusOptions: [
        { text: 'Pending', value: 'pending' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' }
      ]
    }
  },
  computed: {
    adminNotesEditorOption() {
      return {
        placeholder: 'Type admin notes...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }]
          ]
        }
      }
    },
    displayActivityLogs() {
      return (this.selectedAppointment && this.selectedAppointment.appointment_activity_logs) || []    
    },
    canSaveDetail() {
      if (!this.selectedAppointment) return false
      const origStatus = String(this.selectedAppointment.is_status || 'pending').toLowerCase()
      const statusChanged = String(this.editStatus || '').toLowerCase() !== origStatus
      const origNotes = this.cleanAdminNotes(this.selectedAppointment.admin_notes || '')
      const newNotes = this.cleanAdminNotes(this.editAdminNotes || '')
      return statusChanged || (newNotes !== origNotes)
    }
  },
  watch: {
    visible: {
      immediate: true,
      async handler(val) {
        if (val && this.appointmentItem) {
          await this.loadAppointmentDetails()
          this.resetScroll()
        }
      }
    }
  },
  methods: {
    ...mapActions('appointment', ['setAppointmentId', 'updateAppointment']),

    async loadAppointmentDetails() {
      this.selectedAppointment = { ...this.appointmentItem }
      this.editStatus = String(this.appointmentItem.is_status || 'pending').toLowerCase()
      this.editAdminNotes = this.appointmentItem.admin_notes || ''
      this.isIdRevealed = false
      this.isAppointmentLoading = true

      try {
        if (this.setAppointmentId && this.appointmentItem.id) {
          await this.setAppointmentId(this.appointmentItem.id)
          const detailed = this.$store.state.appointment && this.$store.state.appointment.appointmentObject
          if (detailed && detailed.id) {
            this.selectedAppointment = { ...detailed }
            this.editStatus = String(detailed.is_status || this.appointmentItem.is_status || 'pending').toLowerCase()
            this.editAdminNotes = detailed.admin_notes || this.appointmentItem.admin_notes || ''
          }
        }
      } catch (e) {
        Vue.$log.error('Failed to load appointment details:', e)
      } finally {
        this.isAppointmentLoading = false
        this.resetScroll()
      }
    },

    closeAppointment() {
      this.resetScroll()
      this.$emit('close')
    },

    async saveAppointmentChanges() {
      if (!this.canSaveDetail || !this.selectedAppointment || !this.selectedAppointment.id) return
      this.isSavingAppointment = true
      try {
        const payload = {
          id: this.selectedAppointment.id,
          pkg: {
            is_status: this.editStatus,
            admin_notes: this.editAdminNotes
          }
        }
        if (this.updateAppointment) {
          await this.updateAppointment(payload)
        }
        this.$emit('saved')
      } catch (e) {
        Vue.$log.error('Failed to update appointment:', e)
      } finally {
        this.isSavingAppointment = false
      }
    },

    formatTableDate(dateStr) {
      if (!dateStr) return '-'
      const parts = String(dateStr).split('T')[0].split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr
    },
    formatTableTime(timeStr) {
      return !timeStr ? '--:--' : String(timeStr).substring(0, 5)
    },
    getBranchCode(item) {
      if (!item) return '-'
      return ((item.org && (item.org.codename || item.org.name)) || item.org_codename || '-').toUpperCase()
    },
    getDoctorName(item) {
      if (!item || !item.doctor) return '-'
      if (typeof item.doctor === 'string') return item.doctor
      return item.doctor.name || (item.doctor.content && item.doctor.content.name) || '-'
    },
    getDepartmentName(item) {
      if (!item || !item.department) return '-'
      if (typeof item.department === 'string') return item.department
      return item.department.department_name || (item.department.content && item.department.content.title) || '-'
    },
    isBookedForOther(item) {
      if (!item) return false
      const cp = item.contact_person || item
      const hasContact = Boolean(
        (cp.contact_firstname && cp.contact_firstname.trim()) ||
        (cp.contact_lastname && cp.contact_lastname.trim()) ||
        (cp.contact_phone && cp.contact_phone.trim()) ||
        (cp.contact_email && cp.contact_email.trim())
      )
      if (hasContact) return true
      if (item.booking_type) {
        const bType = String(item.booking_type).toLowerCase()
        if (bType === 'other' || bType === 'for other' || bType === 'for_other') return true
      }
      return Boolean(item.booking_for_other)
    },
    bookingTypeClass(item) {
      return this.isBookedForOther(item) ? 'type-pill-other' : 'type-pill-self'
    },
    formatAppointmentBookingType(item) {
      return this.isBookedForOther(item) ? 'Booking for Others' : 'Self Booking'
    },
    calculateAge(birthdate) {
      if (!birthdate) return ''
      const birthYear = parseInt(String(birthdate).split('T')[0].split('-')[0], 10)
      const currentYear = new Date().getFullYear()
      return birthYear && currentYear > birthYear ? `Age ${currentYear - birthYear}` : ''
    },
    formatBirthdate(dateStr) {
      return !dateStr ? '—' : this.formatTableDate(dateStr)
    },
    formatAppointmentDateTime(apt) {
      if (!apt || !apt.apt_date) return '-'
      const date = this.formatTableDate(apt.apt_date)
      const startTime = this.formatTableTime(apt.apt_start_time)
      const endTime = this.formatTableTime(apt.apt_end_time)
      if (startTime !== '--:--' && endTime !== '--:--') {
        return `${date}, ${startTime} – ${endTime}`
      } else if (startTime !== '--:--') {
        return `${date}, ${startTime}`
      }
      return date
    },
    getPatientTitleFirstName(apt) {
      if (!apt) return '—'
      const c = apt.customer || {}
      let prefix = apt.cust_prefix || c.prefix || ''
      if (prefix) prefix = prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase()
      const firstName = c.first_name || (apt.cust_fullname || c.fullname || '').trim().split(/\s+/)[0] || ''
      return prefix ? `${prefix} ${firstName}`.trim() : (firstName || '—')
    },
    getPatientLastName(apt) {
      if (!apt) return '—'
      const c = apt.customer || {}
      if (c.last_name) return c.last_name
      const parts = (apt.cust_fullname || c.fullname || '').trim().split(/\s+/)
      return parts.length > 1 ? parts.slice(1).join(' ') : '—'
    },
    getPatientGender(apt) {
      const g = (apt && (apt.cust_gender || (apt.customer && apt.customer.gender)))
      if (!g) return '—'
      const str = String(g).toLowerCase()
      if (str === 'm' || str === 'male') return 'Male'
      if (str === 'f' || str === 'female') return 'Female'
      return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase()
    },
    getPatientBirthdate(apt) {
      return (apt && (apt.cust_birthdate || (apt.customer && apt.customer.birthdate))) || ''
    },
    getPatientPhone(apt) {
      return (apt && (apt.cust_phone || (apt.customer && apt.customer.phone))) || '—'
    },
    getPatientEmail(apt) {
      return (apt && (apt.cust_email || (apt.customer && apt.customer.email))) || '—'
    },
    getPatientIdCard(apt) {
      return (apt && (apt.cust_id_card_no || (apt.customer && apt.customer.id_card_no))) || '—'
    },
    formatIdCard(id) {
      if (!id) return '—'
      const str = String(id).replace(/\D/g, '')
      return str.length === 13 ? `${str[0]}-${str.substring(1, 5)}-${str.substring(5, 10)}-${str.substring(10, 12)}-${str[12]}` : id
    },
    maskIdCard() {
      return '••• •••• •• •••• •'
    },
    getFileName(file) {
      if (!file) return '—'
      if (typeof file === 'string') {
        const cleanUrl = file.split('?')[0]
        return cleanUrl.split('/').pop() || '-'
      }
      return file.name || file.file_name || file.filename || '-'
    },
    getFileSize(file) {
      if (!file || typeof file === 'string' || !file.size) return ''
      const kb = file.size / 1024
      return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`
    },
    getFileUrl(file) {
      if (!file) return '-'
      if (typeof file === 'string') return file
      return file.url || file.file_url || '-'
    },
    getSubmitterName(apt) {
      const cp = (apt && apt.contact_person) || {}
      return `${cp.contact_firstname || ''} ${cp.contact_lastname || ''}`.trim() || '-'
    },
    getSubmitterRelationship(apt) {
      const cp = (apt && apt.contact_person) || {}
      return cp.contact_relationship || 'Tchild'
    },
    getSubmitterPhone(apt) {
      const cp = (apt && apt.contact_person) || {}
      return cp.contact_phone || '-'
    },
    getSubmitterEmail(apt) {
      const cp = (apt && apt.contact_person) || {}
      return cp.contact_email || '-'
    },
    getEmergencyContactName(apt) {
      const cp = (apt && apt.contact_person) || {}
      const fullName = `${cp.contact_firstname || ''} ${cp.contact_lastname || ''}`.trim()
      const rel = cp.contact_relationship || 'Tfather'
      return fullName && rel ? `${fullName} (${rel})` : (fullName || '—')
    },
    getEmergencyContactPhone(apt) {
      const cp = (apt && apt.contact_person) || {}
      return cp.contact_phone || '—'
    },
    getEmergencyContactEmail(apt) {
      const cp = (apt && apt.contact_person) || {}
      return cp.contact_email || '—'
    },
    formatConsentTimestamp(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      const pad = n => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    cleanAdminNotes(val) {
      return val ? String(val).replace(/<[^>]*>/g, '').trim() : ''
    },
    formatActivityTime(dateStr) {
      if (!dateStr) return '-'
      try {
        const cleanStr = String(dateStr).replace('Z', '')
        const d = new Date(cleanStr)
        if (isNaN(d.getTime())) return dateStr
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        const yesterday = new Date(now)
        yesterday.setDate(yesterday.getDate() - 1)
        const isYesterday = d.toDateString() === yesterday.toDateString()
        const pad = n => String(n).padStart(2, '0')
        const timePart = `${pad(d.getHours())}:${pad(d.getMinutes())}`
        if (isToday) return `Today, ${timePart}`
        if (isYesterday) return `Yesterday, ${timePart}`
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${timePart}`
      } catch (e) {
        return dateStr
      }
    },
    getActivityBulletClass(actionType) {
      return (ACTIVITY_ACTION_CONFIG[actionType] && ACTIVITY_ACTION_CONFIG[actionType].bulletClass) || 'bullet-gray'
    },
    getActivityTitle(log) {
      if (!log) return 'Updated appointment'
      const config = ACTIVITY_ACTION_CONFIG[log.action_type]
      return (config && config.getTitle && config.getTitle(log)) || log.title || 'Updated appointment'
    },
    resetScroll() {
      this.$nextTick(() => {
        if (this.$refs.leftCol) {
          this.$refs.leftCol.scrollTop = 0
        }
        if (this.$refs.rightCol) {
          this.$refs.rightCol.scrollTop = 0
        }
        const card = this.$el && this.$el.querySelector && this.$el.querySelector('.detail-appointment')
        if (card) {
          card.scrollTop = 0
        }
      })
    },
  }
}
</script>

<style scoped>
/* ================= Appointment Layout & Head ================= */
.detail-appointment {
  border-radius: 14px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  background-color: #ffffff !important;
}

.detail-head {
  border-bottom: 1px solid #e6e9f0;
  padding: 16px 22px;
}

.detail-title {
  font-size: 19px !important;
  font-weight: 800 !important;
  line-height: 1.2;
  color: #0f172a !important;
  letter-spacing: -0.2px;
}

.ref-highlight {
  color: #0f172a !important;
}

.detail-sub {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.3;
  margin-top: 4px;
}

.appointment-branch-tag {
  background: #eef2f6 !important;
  color: #475569 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  padding: 3px 8px !important;
  border-radius: 5px !important;
  display: inline-flex;
  align-items: center;
}

.dot-sep {
  color: #94a3b8;
  font-size: 12px;
  margin: 0 4px;
}

.meta-text {
  font-size: 12.5px;
  color: #64748b;
}

.close-btn {
  background: #f1f5f9 !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
}

.detail-body {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  min-height: 460px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.appointment-loading-wrap {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.left-col, .right-col {
  padding: 18px 20px;
  min-width: 0;
}

.left-col {
  overflow-y: auto;
  min-height: 0;
  border-right: 1px solid #e6e9f0;
  background: #fbfcfe;
}

.right-col {
  overflow-y: auto;
  min-height: 0;
  background: #ffffff;
}

/* ================= Summary Card ================= */
.summary-section-label {
  text-transform: uppercase;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 800;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.appointment-summary-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.summary-datetime-text {
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
}

.summary-field-val {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.type-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.2;
}

.type-pill-self {
  background-color: #ebf3fe !important;
  color: #1e40af !important;
}

.type-pill-other {
  background-color: #f5eeff !important;
  color: #7c3aed !important;
}

.symptoms-content-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}

.file-attachment-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: border-color 0.15s ease;
}

.file-attachment-card:hover {
  border-color: var(--primary-500-color);
}

.file-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.file-size {
  font-size: 11.5px;
  color: #64748b;
}

.download-link {
  color: var(--primary-500-color) !important;
  font-size: 12.5px;
  text-decoration: none;
}

.download-link:hover {
  text-decoration: underline;
}

.empty-files-box {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  color: #64748b;
}

/* ================= Submitter Card ================= */
.submitter-card {
  background: #fdfcff;
  border: 1px solid #ede9fe;
  border-left: 3px solid #8b5cf6;
  border-radius: 10px;
}

.submitter-badge-title {
  font-size: 11.5px;
  font-weight: 800;
  color: #7c3aed;
  letter-spacing: 0.4px;
}

.relationship-badge {
  background: #f5f3ff;
  color: #7c3aed;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}

/* ================= Patient Profile & Form Info ================= */
.profile-header-title {
  font-size: 11.5px;
  font-weight: 800;
  color: #7b91af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.profile-val {
  font-size: 14.5px !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  line-height: 1.35;
  margin: 0;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
}

label {
  display: block;
  font-size: 10.5px;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 4px;
  letter-spacing: 0.4px;
}

p {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.sep {
  border: none;
  border-top: 1px solid #e6e9f0;
  margin: 14px 0;
}

.id-display-row {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 1px;
}

.security-warning-box {
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 6px;
  padding: 6px 10px;
}

.lock-icon {
  color: #d97706 !important;
}

.security-text {
  font-size: 11.5px;
  color: #b45309;
  font-weight: 500;
}

.reveal-btn {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* ================= PDPA Legal Consent Audit ================= */
.pdpa-audit {
  border: 1px solid #dbeafe;
  background: #f8fbff;
  border-radius: 10px;
  padding: 14px 16px;
}

.pdpa-audit-head {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.pdpa-lock-icon {
  color: #1e3a8a !important;
  font-size: 15px !important;
}

.pdpa-audit-title {
  font-size: 13.5px;
  font-weight: 800;
  color: #1e3a8a;
  letter-spacing: -0.2px;
}

.pdpa-audit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  gap: 10px 14px;
}

.audit-label {
  font-size: 10.5px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
  display: block;
}

.consent-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 3px 9px;
  line-height: 1.2;
}

.consent-chip.accepted {
  background: #e6f9ed;
  color: #15803d;
}

.consent-icon {
  color: #15803d !important;
  font-size: 13px !important;
  font-weight: 900;
}

.mono-val {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
  margin: 0;
}

.audit-footnote {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #cfe0ff;
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.45;
  font-weight: 400;
}

/* ================= Right Column: Quill & Activity ================= */
.section-title {
  text-transform: uppercase;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 800;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.admin-notes-editor {
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.admin-notes-editor ::v-deep .ql-toolbar.ql-snow {
  border: none;
  border-bottom: 1px solid #e6e9f0;
  background: #f8fafc;
  padding: 8px 10px;
}

.admin-notes-editor ::v-deep .ql-container.ql-snow {
  border: none;
  min-height: 150px;
  font-family: inherit;
  font-size: 13px;
  color: #1e293b;
}

.admin-notes-editor ::v-deep .ql-editor {
  min-height: 150px;
  line-height: 1.6;
}

.admin-notes-editor ::v-deep .ql-editor.ql-blank::before {
  font-style: normal;
  color: #94a3b8;
  font-size: 13px;
}

.hint-text {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 6px;
}

.activity-header {
  display: flex;
  align-items: center;
}

.activity-header-title {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.activity-timeline {
  display: flex;
  flex-direction: column;
}

.activity-item {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}

.activity-bullet {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
}

.bullet-blue { background-color: #2563eb; }
.bullet-amber { background-color: #eab308; }
.bullet-gray { background-color: #94a3b8; }

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-title {
  font-size: 13px;
  color: #1e293b;
  font-weight: 400;
  line-height: 1.4;
}

.status-arrow {
  margin: 0 4px;
  color: #64748b;
  font-weight: normal;
}

.activity-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.meta-dot {
  margin: 0 4px;
  color: #94a3b8;
}

.activity-badge {
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1px 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.activity-note-box {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
  word-break: break-word;
}

.activity-divider {
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 14px 0 0;
  width: 100%;
}

.activity-empty-box {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 12px;
}

/* ================= Footer ================= */
.detail-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid #e6e9f0;
  background: #ffffff;
}

.save-btn {
  color: #ffffff !important;
  font-weight: 600;
  border-radius: 6px;
}

.save-btn.v-btn--disabled {
  background-color: #f1f5f9 !important;
  color: #94a3b8 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}

.cancel-btn {
  font-weight: 600;
  border-color: #e2e8f0 !important;
}
</style>
